import { toSportLabel, toSportSlug } from './sports';
import { UserRole } from '@types/index';

export type ContestPortalMode = 'player' | 'coach';

export function contestPortalModeForUser(user?: { role?: string } | null): ContestPortalMode {
  return String(user?.role || '') === UserRole.COACH || String(user?.role || '') === 'coach'
    ? 'coach'
    : 'player';
}

/** Signed-in contest sport page (player/coach shell + sidebar). */
export function buildPortalContestPath(
  sport: string,
  options?: {
    tab?: string | null;
    age?: string | null;
    contest?: string | null;
    category?: string;
    portal?: ContestPortalMode;
  },
): string {
  const slug = toSportSlug(sport);
  const category = String(options?.category || 'all').toLowerCase();
  const portal = options?.portal || 'player';
  const params = new URLSearchParams();
  if (options?.tab) params.set('tab', options.tab);
  if (options?.age) params.set('age', options.age);
  if (options?.contest) params.set('contest', options.contest);
  const query = params.toString();
  return `/${portal}/contests/${slug}/${category}${query ? `?${query}` : ''}`;
}

/**
 * Map a public `/contests/:sport...` URL to the signed-in sport page so login
 * returns to the same tab/age instead of the contest hub.
 */
export function mapPublicContestUrlToPortal(
  user: { role?: string } | null | undefined,
  url: string,
): string {
  if (!url.startsWith('/contests/')) return url;
  const [rawPath, rawQuery = ''] = url.split('?');
  const parts = rawPath.split('/').filter(Boolean);
  const sportPart = parts[1];
  if (!sportPart) return url;
  const sport = toSportLabel(decodeURIComponent(sportPart));
  const params = new URLSearchParams(rawQuery);
  const leaf = parts[2];
  if (leaf === 'self-evaluation') params.set('tab', 'self-evaluation');
  if (leaf === 'leaderboard') params.set('tab', 'leaderboard');
  return buildPortalContestPath(sport, {
    tab: params.get('tab'),
    age: params.get('age'),
    contest: params.get('contest'),
    portal: contestPortalModeForUser(user),
  });
}

/** Player contest catalog URL with selected skill + upload panel open. */
export function buildPlayerContestUploadPath(
  sport: string,
  contestId: string,
  ageCategory: string,
  category = 'all'
): string {
  const slug = toSportSlug(sport);
  const params = new URLSearchParams({
    contest: contestId,
    age: ageCategory,
    action: 'upload',
  });
  return `/player/contests/${slug}/${category}?${params.toString()}`;
}

/** Login URL that returns to contest upload after sign-in. */
export function buildContestSubmitLoginPath(
  sport: string,
  contestId: string,
  ageCategory: string,
  category = 'all'
): string {
  const returnUrl = buildPlayerContestUploadPath(sport, contestId, ageCategory, category);
  return `/login?returnUrl=${encodeURIComponent(returnUrl)}`;
}
